import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import api from 'apis/api';

import { useThreads } from 'hooks/maxi-ai/useThreads';

import { config as env } from 'utils/config/env';
import { featureFlags } from 'utils/config/featureFlags';
import { Logger } from 'utils/logger';
import {
  createLoadingArtifact,
  getArtifactType,
} from 'utils/maxi-ai/artifactParser';

import { useMaxiAIStore } from 'store/maxi-ai.store';
import { useTokenStore } from 'store/token.store';

import type {
  ChatUpdate,
  ChatMessage,
  ThoughtProcess,
  Thought,
  Source,
} from 'types/maxi-ai';

// Azumi POC: hardcoded thought process steps
const AZUMI_MOCK_THOUGHTS: Thought[] = [
  { thoughtText: 'Search data', urls: [] },
  { thoughtText: 'Applied filters', urls: [] },
  {
    thoughtText: 'Analyzed data',
    urls: [],
    description:
      'Successfully compiled an analysis based on available data sources',
  },
];

const MOCK_RESPONSES: Record<string, string> = {
  'What is driving the change in sales compared with the same week last year?':
    'Based on **Oracle Micros** data, restaurant revenue is down **15%**, making it the largest contributor to the overall **5% decline** in sales this week. \n\n> **Note**: This decline is partially offset by strong performance in other categories.',
  'How is lunch performing compared with the same period last year?':
    'Lunch is a 🟢 strong performer this period, adding an additional **$50,000 in sales**. This represents a **15% increase** compared to the same period last year.',
  'What is the change in food sales this period?':
    'Total food sales have seen a decline. The largest impact comes from **Sushi**, **Maki**, and **Sashimi**, which together contributed to a 📉 **20% decline** compared with the same period last year.',
  'What is the change in wine sales this period?':
    '🍷**Red wine** is currently 🔴 underperforming. This stems from lower volume sold in the category: **100 units** this year vs **158 units** last year.',
  'What is the breakdown of complimentary items?':
    'The top 3 contributors to complimentary items (Comps) are:\n\n1. **Comp Manager Meal**: 100%\n2. **Comp Poor Service**: 100%\n3. **Training/Testing**: 100%',
  'What is the change in covers across locations?':
    'According to **Sevenrooms** data, the biggest drop in covers is coming from **Restaurant Lunch**. \n\n*   **Weekly Change**: -50 covers\n*   **YTD Change**: -250 covers',
};

// Find the last thought process in messages
const findLastThoughtProcessIndex = (messages: any[]): number => {
  return (
    messages
      .map((msg, idx) => ({ msg, idx }))
      .reverse()
      .find(({ msg }) => msg.type === 'THOUGHT_PROCESS')?.idx ?? -1
  );
};

export const useMaxiAI = () => {
  const { t } = useTranslation();

  // User-friendly error messages
  const getErrorMessage = useCallback(
    (error: Error): string => {
      const message = error.message;

      if (message.includes('401') || message.includes('403')) {
        return t('errors.authenticationError');
      }
      if (message.includes('429')) {
        return t('errors.tooManyRequests');
      }
      if (
        message.includes('500') ||
        message.includes('502') ||
        message.includes('503')
      ) {
        return t('errors.serverError');
      }
      if (message.includes('network') || message.includes('fetch')) {
        return t('errors.networkError');
      }
      if (
        message.includes(
          'Failed to connect to central data hub using Rutter integrations'
        )
      ) {
        return t('errors.integrationConnectionError');
      }

      return t('errors.genericError');
    },
    [t]
  );
  const { accessToken } = useTokenStore();
  const {
    messages,
    threadId,
    artifacts,
    isLoading: storeIsLoading,
    updateMessages,
    setThreadId,
    threads,
    setThreads,
    setArtifact,
    updateArtifact,
    clearArtifacts,
    isMockMode,
  } = useMaxiAIStore();

  const { refetch: refetchThreads } = useThreads();

  const addFeedback = useCallback(
    async (traceId: string, thumbsUp?: boolean, feedback?: string) => {
      await api.post('/api/v2/maxi-ai/feedback', {
        trace_id: traceId,
        thumbs_up: thumbsUp,
        feedback: feedback,
      });
    },
    []
  );

  // Mark the most recent thought process as complete
  const markThoughtProcessComplete = useCallback(() => {
    updateMessages((msgs) => {
      const messagesCopy = msgs.slice();
      const thoughtIndex = findLastThoughtProcessIndex(messagesCopy);

      if (
        thoughtIndex !== -1 &&
        messagesCopy[thoughtIndex].type === 'THOUGHT_PROCESS'
      ) {
        messagesCopy[thoughtIndex] = {
          ...messagesCopy[thoughtIndex],
          end: dayjs().valueOf(),
        };
      }
      return messagesCopy;
    });
  }, [updateMessages]);

  // Update current thought in progress
  const updateCurrentThought = useCallback(
    (thoughtText: string) => {
      updateMessages((msgs) => {
        const messagesCopy = msgs.slice();
        const thoughtProcess = messagesCopy[messagesCopy.length - 2];

        if (thoughtProcess.type !== 'THOUGHT_PROCESS') return messagesCopy;

        messagesCopy[messagesCopy.length - 2] = {
          ...thoughtProcess,
          currentThought: {
            urls: [],
            thoughtText,
          },
        };
        return messagesCopy;
      });
    },
    [updateMessages]
  );

  // Move current thought to completed thoughts
  const completeCurrentThought = useCallback(
    (update: ChatUpdate) => {
      updateMessages((msgs) => {
        const messagesCopy = msgs.slice();
        const thoughtProcess = messagesCopy[messagesCopy.length - 2];

        if (thoughtProcess.type !== 'THOUGHT_PROCESS') return messagesCopy;

        messagesCopy[messagesCopy.length - 2] = {
          ...thoughtProcess,
          completeThoughts: [
            ...thoughtProcess.completeThoughts,
            {
              urls: update.urls || [],
              thoughtText: update.complete || '',
              description: update.description,
            },
          ],
          currentThought: undefined,
        };
        return messagesCopy;
      });
    },
    [updateMessages]
  );

  // Update bot message with streaming response
  const updateBotMessage = useCallback(
    (responseText: string) => {
      updateMessages((msgs) => {
        const messagesCopy = msgs.slice();
        const botMessage = messagesCopy[messagesCopy.length - 1];

        if (botMessage.type !== 'CHAT_MESSAGE' || botMessage.isUser) {
          return messagesCopy;
        }

        messagesCopy[messagesCopy.length - 1] = {
          ...botMessage,
          text: responseText,
        };

        return messagesCopy;
      });
    },
    [updateMessages]
  );

  // Handle artifact status updates
  const handleArtifactStatus = useCallback(
    (update: ChatUpdate) => {
      const {
        artifact_id,
        artifact_type,
        artifact_status,
        artifact_content,
        artifact_error,
      } = update;

      if (!artifact_id) return;

      // For processing status, we need the artifact_type to create the artifact
      if (artifact_status === 'processing') {
        if (!artifact_type) return;
        const validArtifactType = getArtifactType(artifact_type);
        if (!validArtifactType) return;

        const loadingArtifact = createLoadingArtifact(
          artifact_id,
          validArtifactType
        );
        setArtifact(artifact_id, loadingArtifact);
      } else if (artifact_status === 'complete' && artifact_content) {
        // Update artifact with content
        // Parse JSON string if needed
        let parsedContent = artifact_content;
        if (typeof artifact_content === 'string') {
          try {
            parsedContent = JSON.parse(artifact_content);
          } catch (e) {
            Logger.error('Failed to parse artifact content:', e);

            // Ensure artifact exists before updating to failed state
            const existingArtifact = artifacts[artifact_id];
            if (!existingArtifact) {
              // Create minimal artifact if it doesn't exist
              const validArtifactType = getArtifactType(
                artifact_type || 'table'
              );
              if (validArtifactType) {
                const minimalArtifact = createLoadingArtifact(
                  artifact_id,
                  validArtifactType
                );
                setArtifact(artifact_id, minimalArtifact);
              }
            }

            // Transition artifact to failed state with parse error
            updateArtifact(artifact_id, (artifact) => ({
              ...artifact,
              status: 'failed',
              error: `Failed to parse artifact content: ${e instanceof Error ? e.message : 'Invalid JSON'}`,
            }));
            return;
          }
        }

        // Ensure artifact exists before updating
        const existingArtifact = artifacts[artifact_id];
        if (!existingArtifact) {
          // Create minimal artifact if it doesn't exist
          const validArtifactType = getArtifactType(artifact_type || 'table');
          if (validArtifactType) {
            const minimalArtifact = createLoadingArtifact(
              artifact_id,
              validArtifactType
            );
            setArtifact(artifact_id, minimalArtifact);
          }
        }

        updateArtifact(artifact_id, (artifact) => ({
          ...artifact,
          status: 'complete',
          content: parsedContent as any, // Type assertion needed due to union type complexity
        }));
      } else if (artifact_status === 'complete' && !artifact_content) {
        // Complete status received but no content - this shouldn't happen
      } else if (artifact_status === 'failed') {
        // Ensure artifact exists before updating
        const existingArtifact = artifacts[artifact_id];
        if (!existingArtifact) {
          // Create minimal artifact if it doesn't exist
          const validArtifactType = getArtifactType(artifact_type || 'table');
          if (validArtifactType) {
            const minimalArtifact = createLoadingArtifact(
              artifact_id,
              validArtifactType
            );
            setArtifact(artifact_id, minimalArtifact);
          }
        }

        // Update artifact with error
        updateArtifact(artifact_id, (artifact) => ({
          ...artifact,
          status: 'failed',
          error: artifact_error || 'Unknown error',
        }));
      }
    },
    [artifacts, setArtifact, updateArtifact]
  );

  // Handle new thread creation - simplified to avoid stale closure
  const handleNewThread = useCallback(
    (newThreadId: string) => {
      if (newThreadId && newThreadId !== threadId) {
        setThreadId(newThreadId);
      }
    },
    [threadId, setThreadId]
  );

  // Handle title updates for new threads
  const handleTitleUpdate = useCallback(
    async (title: string, newThreadId: string) => {
      if (threadId) {
        // Update existing thread
        setThreads(
          threads.map((thread) =>
            thread.id === threadId ? { ...thread, title } : thread
          )
        );
      } else if (newThreadId) {
        // Create new thread with title - add optimistically first
        const newThread = {
          id: newThreadId,
          title,
          organization_id: '',
          created_at: dayjs().toISOString(),
          pinned: false,
        };
        setThreads([newThread, ...threads]);
        setThreadId(newThreadId);

        // Refetch threads from backend to ensure correct ordering
        // This will update the threads list with the actual server data
        try {
          await refetchThreads();
        } catch (error) {
          Logger.error('Failed to refetch threads after creation:', error);
          // Keep the optimistic update if refetch fails
        }
      }
    },
    [threadId, threads, setThreads, setThreadId, refetchThreads]
  );

  // Handle chat errors
  const { showBoundary } = useErrorBoundary();

  const handleChatError = useCallback(
    (error: Error) => {
      Logger.error('Chat error:', error);

      // For critical errors, show error boundary
      if (error.message.includes('Network') || error.message.includes('500')) {
        showBoundary(error);
        return;
      }

      const errorMessage: ChatMessage = {
        type: 'CHAT_MESSAGE',
        id: dayjs().valueOf().toString(),
        text: getErrorMessage(error),
        isUser: false,
        timestamp: dayjs().toDate(),
      };

      updateMessages((msgs) => {
        let messagesCopy = msgs.slice();

        // Remove any incomplete messages (loading states)
        const filteredMessages = messagesCopy.filter((msg) => {
          if (msg.type === 'CHAT_MESSAGE' && !msg.isUser && !msg.text) {
            return false; // Remove empty bot messages
          }
          if (msg.type === 'THOUGHT_PROCESS' && !msg.end) {
            return false; // Remove incomplete thought processes
          }
          return true;
        });

        // Only add error message if the last message isn't already an error
        const lastMessage = filteredMessages[filteredMessages.length - 1];
        messagesCopy = filteredMessages;
        if (
          !lastMessage ||
          lastMessage.type !== 'CHAT_MESSAGE' ||
          lastMessage.isUser ||
          !lastMessage.text?.includes('Sorry')
        ) {
          messagesCopy.push(errorMessage);
        }

        return messagesCopy;
      });
    },
    [updateMessages, showBoundary, getErrorMessage]
  );

  // Finalize response and save thread_id
  const finalizeResponse = useCallback(
    (data: ChatUpdate) => {
      if (data.thread_id) {
        handleNewThread(data.thread_id);
      }

      updateMessages((msgs) => {
        const messagesCopy = msgs.slice();
        const botMessage = messagesCopy[messagesCopy.length - 1];

        if (botMessage.type === 'CHAT_MESSAGE' && !botMessage.isUser) {
          messagesCopy[messagesCopy.length - 1] = {
            ...botMessage,
            trace_id: data.trace_id,
          };
        }
        return messagesCopy;
      });
      markThoughtProcessComplete();
    },
    [handleNewThread, updateMessages, markThoughtProcessComplete]
  );

  // Handle different types of chat updates
  const handleChatUpdate = useCallback(
    (update: ChatUpdate) => {
      if (update.title && update.thread_id) {
        handleTitleUpdate(update.title, update.thread_id);
      }
      if (update.current) {
        updateCurrentThought(update.current);
      } else if (update.complete) {
        completeCurrentThought(update);
        if (update.thread_id) {
          handleNewThread(update.thread_id);
        }
      } else if (update.response) {
        updateBotMessage(update.response);
      } else if (update.artifact_status) {
        handleArtifactStatus(update);
      }
    },
    [
      updateCurrentThought,
      completeCurrentThought,
      updateBotMessage,
      handleArtifactStatus,
      handleTitleUpdate,
      handleNewThread,
    ]
  );

  // Process streaming response from chat API
  const processStreamingResponse = useCallback(
    async (response: Response) => {
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body reader not available');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedResponse = '';
      let isComplete = false;

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim()) continue;

            const data: ChatUpdate = JSON.parse(line.trim());

            if (data.error) {
              throw new Error(data.error);
            }

            if (data.response_chunk) {
              accumulatedResponse += data.response_chunk;
              handleChatUpdate({ ...data, response: accumulatedResponse });
            } else if (data.artifact_status) {
              handleChatUpdate(data);
            } else if (data.done) {
              finalizeResponse(data);
              isComplete = true;
              return { response: accumulatedResponse || '' };
            } else {
              handleChatUpdate(data);
            }
          }
        }
      } finally {
        // Process remaining buffer
        if (buffer.trim()) {
          try {
            const data = JSON.parse(buffer.trim());
            if (data.response_chunk) {
              accumulatedResponse += data.response_chunk;
              handleChatUpdate({ ...data, response: accumulatedResponse });
            } else if (data.artifact_status) {
              handleChatUpdate(data);
            } else if (data.done) {
              finalizeResponse(data);
              isComplete = true;
            } else {
              handleChatUpdate(data);
            }
          } catch (parseError) {
            Logger.warn(
              'Failed to parse final JSON buffer:',
              buffer,
              parseError
            );
          }
        }
      }

      // Mark complete if not already done
      if (!isComplete) {
        markThoughtProcessComplete();
      }

      return { response: accumulatedResponse };
    },
    [handleChatUpdate, finalizeResponse, markThoughtProcessComplete]
  );

  // Send message mutation
  const {
    mutate: sendMessageMutation,
    isPending: isSending,
    error,
  } = useMutation({
    mutationFn: useCallback(
      async ({
        request,
      }: {
        request: {
          messageText: string;
          organization_id: string;
          sources: Source[];
        };
      }) => {
        const { messageText, organization_id, sources } = request;

        const url = `api/v2/maxi-ai/chat?organization_id=${organization_id}`;
        const fullUrl = `${env.VITE_API_URL}${url}`;

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        const requestBody: any = {
          new_message: messageText,
          sources,
        };

        // Only include thread_id if threadId is set
        if (threadId) requestBody.thread_id = threadId;

        const response = await fetch(fullUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
        });

        // Handle HTTP errors
        if (!response.ok) {
          const status = response.status;
          const errorMessage =
            status === 401 || status === 403
              ? `Authentication error: ${status}`
              : status === 429
                ? `Rate limit exceeded: ${status}`
                : status >= 500
                  ? `Server error: ${status}`
                  : `HTTP error! status: ${status}`;

          throw new Error(errorMessage);
        }

        return await processStreamingResponse(response);
      },
      [accessToken, threadId, processStreamingResponse]
    ),

    onError: handleChatError,
  });

  // Send a new message and initialize UI state
  const sendMessage = useCallback(
    (messageText: string, organization_id: string, sources: Source[]) => {
      if (!messageText.trim() || isSending) return;

      const now = dayjs().valueOf();

      // Create message objects
      const userMessage: ChatMessage = {
        type: 'CHAT_MESSAGE',
        id: now.toString(),
        text: messageText,
        isUser: true,
        timestamp: dayjs().toDate(),
      };

      const isAzumiPOC = featureFlags.FF__AZUMI_POC;

      const thoughtProcess: ThoughtProcess = {
        type: 'THOUGHT_PROCESS',
        id: (now + 1).toString(),
        start: now,
        completeThoughts: isAzumiPOC ? AZUMI_MOCK_THOUGHTS : [],
        ...(isAzumiPOC ? { end: now } : {}),
      };

      const botResponse: ChatMessage = {
        type: 'CHAT_MESSAGE',
        id: (now + 2).toString(),
        text: '', // Start with empty text that will be filled by streaming
        isUser: false,
        timestamp: dayjs().toDate(),
      };

      // Add messages to state and trigger API call
      updateMessages((msgs) => {
        // If no threadId, start a new conversation (clear existing messages)
        const baseMessages = threadId ? msgs : [];
        return [...baseMessages, userMessage, thoughtProcess, botResponse];
      });

      // Clear artifacts if starting a new conversation
      if (!threadId) {
        clearArtifacts();
      }

      if (isMockMode) {
        // Mock response logic for POC
        setTimeout(() => {
          const responseText =
            MOCK_RESPONSES[messageText] ||
            "I'm sorry, I don't have the specific data for that request in this POC. Try asking about top locations or labor costs!";

          updateBotMessage(responseText);
          markThoughtProcessComplete();
        }, 1500);
        return;
      }

      sendMessageMutation({
        request: { messageText, organization_id, sources },
      });
    },
    [
      isSending,
      sendMessageMutation,
      updateMessages,
      threadId,
      clearArtifacts,
      isMockMode,
      updateBotMessage,
      markThoughtProcessComplete,
    ]
  );

  return {
    messages,
    threadId,
    artifacts,
    sendMessage,
    addFeedback,
    isLoading: storeIsLoading || isSending,
    error,
  };
};
